import { Injectable } from '@angular/core';
import { userDtos } from '@vectronic/openApi-customer';
import { L } from '../leaflet-setup/leaflet-setup';

@Injectable({ providedIn: 'root' })
export class MapBaseLayersService {
  private map: L.Map;
  private userPreferences: userDtos.UserPreferences;

  private googleRoadMap: L.GridLayer;
  private googleSatelliteMap: L.GridLayer;
  private googleHybridMap: L.GridLayer;
  private googleTerrainMap: L.GridLayer;

  constructor() {
    const commonOptions: Partial<L.gridLayer.GoogleMutantOptions> = {
      updateWhenZooming: false, // Keep old tiles scaled during zoom animation
      updateWhenIdle: true, // Update tiles when user stops panning
      keepBuffer: 4, // Keep more nearby tiles cached
    };

    this.googleRoadMap = L.gridLayer.googleMutant?.({
      ...commonOptions,
      type: 'roadmap',
    });

    this.googleSatelliteMap = L.gridLayer.googleMutant?.({
      ...commonOptions,
      type: 'satellite',
      styles: [{ featureType: 'all', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
    });

    this.googleHybridMap = L.gridLayer.googleMutant?.({
      ...commonOptions,
      type: 'hybrid',
    });

    this.googleTerrainMap = L.gridLayer.googleMutant?.({
      ...commonOptions,
      type: 'terrain',
    });
  }

  initialize(map: L.Map, userPreferences: userDtos.UserPreferences) {
    this.map = map;
    this.userPreferences = userPreferences;
  }

  /** Re-points the service at a surviving map without touching preferences. */
  reattach(map: L.Map) {
    this.map = map;
  }

  setMapType(mapType: 'roadmap' | 'satellite' | 'terrain' | 'hybrid') {
    const mapProviderMapping: Record<'satellite' | 'terrain' | 'roadmap' | 'hybrid', number> = {
      satellite: 0,
      terrain: 1,
      roadmap: 2,
      hybrid: 3,
    };

    if (this.userPreferences) {
      this.userPreferences.mapProvider = mapProviderMapping[mapType];
    }

    if (!this.map) {
      return;
    }

    const layerMap = {
      roadmap: this.googleRoadMap,
      satellite: this.googleSatelliteMap,
      terrain: this.googleTerrainMap,
      hybrid: this.googleHybridMap,
    };

    const newLayer = layerMap[mapType];
    if (!newLayer) {
      return;
    }

    // Add the new layer first to prevent flickering
    if (!this.map.hasLayer(newLayer)) {
      newLayer.addTo(this.map);
    }

    // Remove other layers
    Object.values(layerMap).forEach((layer) => {
      if (layer && layer !== newLayer && this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    });
  }

  restoreFromPreferences() {
    if (!this.map) return;
    const layer = this.getInitialLayer();
    if (layer && !this.map.hasLayer(layer)) layer.addTo(this.map);
  }

  getInitialLayer(): L.GridLayer | undefined {
    const state = this.userPreferences;
    if (!state) return this.googleSatelliteMap;

    const mapTypeMapping = {
      0: this.googleSatelliteMap,
      1: this.googleTerrainMap,
      2: this.googleRoadMap,
      3: this.googleHybridMap,
    };

    return mapTypeMapping[state.mapProvider] || this.googleSatelliteMap;
  }

  clearBaseLayers() {
    if (!this.map) return;

    const baseLayers = [
      this.googleRoadMap,
      this.googleSatelliteMap,
      this.googleHybridMap,
      this.googleTerrainMap,
    ];

    baseLayers.forEach((layer) => {
      if (layer && this.map.hasLayer(layer)) {
        this.map.removeLayer(layer);
      }
    });
  }
}
